import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import { getPaygClients } from '../../../../../../jsx/actions/clientAction';
import { fetchSubscriptions } from '../../../../../../jsx/actions/subscriptionAction';
import { saveIntegrationBoxes, resetIntegrationBoxesSaveError } from '../../../../../../jsx/actions/settingsAction';

var Moment = require('moment');

const INTEGRATION_TYPE = 'prospect';
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

function formatDate(value) {
    return value ? Moment(value).format('DD/MM/YYYY') : '—';
}

/**
 * Modal de sélection des box (associations box/client) envoyées à Prospect — liste blanche stricte.
 * Lignes = liste Redux de la page Home (state.clients[boxAssoId].resume, chargée par getPaygClients).
 * L'édition reste locale (Set) jusqu'à « Enregistrer », qui remplace la liste complète côté serveur.
 * Le compte peut compter ~20 000 box : filtrage mémoïsé, pagination obligatoire, Set pour la sélection.
 * À monter seulement quand il est ouvert : la sélection locale est initialisée au montage.
 */
function ProspectBoxSelectionDialog(props) {
    const { CLIENTS, SUBSCRIPTIONS, STATE, boxes, onClose, onSaved } = props;
    const savedSelected = boxes && boxes.selected;
    const initialSelected = useMemo(() => savedSelected || [], [savedSelected]);
    const synced = (boxes && boxes.synced) || {};
    const savedExcluded = boxes && boxes.excluded;
    const excluded = useMemo(() => savedExcluded || {}, [savedExcluded]);
    const saving = !!(boxes && boxes.saving);
    const saveError = boxes && boxes.saveError;

    const [selection, setSelection] = useState(() => new Set(initialSelected));
    const [search, setSearch] = useState('');
    const [show, setShow] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

    const clientsFetching = !!(STATE && STATE.paygClients && STATE.paygClients.fetching);
    const clientsFetched = !!(STATE && STATE.paygClients && STATE.paygClients.fetched);

    // Même garde que la page Home : on ne recharge que ce qui ne l'est pas encore.
    useEffect(() => {
        props.resetIntegrationBoxesSaveError(INTEGRATION_TYPE);
        if (!clientsFetching && !clientsFetched) {
            props.getPaygClients();
        }
        if (isEmpty(SUBSCRIPTIONS)) {
            props.fetchSubscriptions();
        }
        // Chargement unique à l'ouverture.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lignes de base, plus récentes en premier ; le nom d'abonnement et l'horodatage de tri
    // sont précalculés une fois par ligne (recherche et tri sur ~20 000 lignes).
    const rows = useMemo(() => {
        const list = [];
        Object.keys(CLIENTS || {}).forEach((ID) => {
            const resume = CLIENTS[ID] && CLIENTS[ID].resume;
            if (!resume || !resume.boxAssoId) return;
            const subscription = SUBSCRIPTIONS && SUBSCRIPTIONS[resume.subscriptionID];
            list.push({
                boxAssoId: resume.boxAssoId,
                idBox: resume.idBox || '',
                clientName: resume.clientName || '',
                subscriptionName: (subscription && subscription.name) || resume.subscriptionID || '',
                timeCreation: resume.timeCreation || '',
                sortTime: (resume.timeCreation && new Date(resume.timeCreation).getTime()) || 0,
            });
        });
        list.sort((a, b) => b.sortTime - a.sortTime);
        return list;
    }, [CLIENTS, SUBSCRIPTIONS]);

    // Box sélectionnées absentes de la liste chargée (association supprimée, liste pas encore
    // chargée...) : comptées à part pour que « n sur N » ne dépasse jamais N.
    const rowIds = useMemo(() => new Set(rows.map((row) => row.boxAssoId)), [rows]);
    const outsideCount = useMemo(() => {
        let count = 0;
        selection.forEach((id) => { if (!rowIds.has(id)) count += 1; });
        return count;
    }, [selection, rowIds]);

    // Filtre texte d'abord (indépendant de la sélection), puis filtre « Afficher ».
    const searched = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((row) =>
            String(row.idBox).toLowerCase().includes(term)
            || String(row.clientName).toLowerCase().includes(term)
            || String(row.subscriptionName).toLowerCase().includes(term));
    }, [rows, search]);

    const filtered = useMemo(() => {
        if (show === 'selected') return searched.filter((row) => selection.has(row.boxAssoId));
        if (show === 'unselected') return searched.filter((row) => !selection.has(row.boxAssoId));
        if (show === 'excluded') return searched.filter((row) => selection.has(row.boxAssoId) && excluded[row.boxAssoId]);
        return searched;
    }, [searched, show, selection, excluded]);

    // Ajouts en attente (cochées localement, absentes de la liste enregistrée) : ils déclenchent
    // le renvoi de tout l'historique des paiements, d'où l'avertissement avant d'enregistrer.
    const pendingAdditions = useMemo(() => {
        const saved = new Set(initialSelected);
        let count = 0;
        selection.forEach((id) => { if (!saved.has(id)) count += 1; });
        return count;
    }, [selection, initialSelected]);

    // Box cochées écartées à la dernière synchro (selon la sélection locale en cours d'édition),
    // séparées en deux : celles qui ont une ligne (visibles via le filtre « Exclues ») et celles
    // absentes de la liste chargée (ex. « association introuvable »), qu'on ne peut retirer
    // qu'en bloc. Un absent non exclu peut simplement ne pas être encore chargé : on n'y touche pas,
    // et tant que la liste n'est pas chargée, aucune box n'est déclarée introuvable.
    const listLoaded = clientsFetched && !clientsFetching;
    const { excludedListedCount, unknownExcludedIds } = useMemo(() => {
        let listed = 0;
        const unknown = [];
        selection.forEach((id) => {
            if (!excluded[id]) return;
            if (rowIds.has(id)) listed += 1; else if (listLoaded) unknown.push(id);
        });
        return { excludedListedCount: listed, unknownExcludedIds: unknown };
    }, [selection, excluded, rowIds, listLoaded]);

    const filteredSelectedCount = useMemo(
        () => filtered.reduce((n, row) => (selection.has(row.boxAssoId) ? n + 1 : n), 0),
        [filtered, selection],
    );
    const allFilteredSelected = filtered.length > 0 && filteredSelectedCount === filtered.length;
    const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;

    // Recadre la page quand la liste filtrée rétrécit (ex. décocher en vue « Sélectionnées »).
    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
        if (page > lastPage) {
            setPage(lastPage);
        }
    }, [filtered.length, rowsPerPage, page]);

    const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleToggle = (boxAssoId) => {
        setSelection((prev) => {
            const next = new Set(prev);
            if (next.has(boxAssoId)) next.delete(boxAssoId); else next.add(boxAssoId);
            return next;
        });
    };

    // Case d'en-tête : agit sur toutes les lignes filtrées (pas seulement la page affichée).
    const handleToggleAllFiltered = () => {
        setSelection((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach((row) => next.delete(row.boxAssoId));
            } else {
                filtered.forEach((row) => next.add(row.boxAssoId));
            }
            return next;
        });
    };

    const handleRemoveUnknown = () => {
        setSelection((prev) => {
            const next = new Set(prev);
            unknownExcludedIds.forEach((id) => next.delete(id));
            return next;
        });
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const handleShow = (e) => {
        setShow(e.target.value);
        setPage(0);
    };

    const handleSave = async () => {
        const result = await props.saveIntegrationBoxes(INTEGRATION_TYPE, Array.from(selection));
        if (result && result.ok) {
            if (onSaved) onSaved(result);
            onClose();
        }
    };

    const renderStatus = (boxAssoId) => {
        const isSelected = selection.has(boxAssoId);
        const syncedAt = synced[boxAssoId];
        // Le motif d'exclusion ne concerne que les box cochées (une box décochée n'est de toute façon pas envoyée).
        if (isSelected && excluded[boxAssoId]) {
            return (
                <Box>
                    {/* Motif libre côté backend, parfois long : puce tronquée, libellé complet en info-bulle. */}
                    <Tooltip title={excluded[boxAssoId]}>
                        <Chip size="small" label={`Exclue : ${excluded[boxAssoId]}`} style={{ backgroundColor: '#fff3e0', color: '#e65100', maxWidth: 260 }} />
                    </Tooltip>
                    {syncedAt && (
                        <Typography variant="caption" color="textSecondary" component="div">
                            dernier envoi le {formatDate(syncedAt)}
                        </Typography>
                    )}
                </Box>
            );
        }
        if (isSelected && syncedAt) {
            return <Chip size="small" label={`Synchronisée le ${formatDate(syncedAt)}`} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />;
        }
        if (isSelected) {
            return <Chip size="small" label="En attente" style={{ backgroundColor: '#fff8e1', color: '#f57f17' }} />;
        }
        if (syncedAt) {
            return <Chip size="small" label={`Retirée (envoyée le ${formatDate(syncedAt)})`} style={{ backgroundColor: '#eeeeee', color: '#616161' }} />;
        }
        return null;
    };

    const loadingRows = clientsFetching && rows.length === 0;

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Box envoyées à Prospect</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Seules les box cochées sont envoyées à Prospect (clients, installations et paiements).
                    Aucune box cochée : rien n'est envoyé.
                </Typography>

                <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 16 }} mb={1}>
                    <TextField
                        label="Rechercher (Paygo Id, client, abonnement)"
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={handleSearch}
                        style={{ minWidth: 320, flex: 1 }}
                    />
                    <RadioGroup row value={show} onChange={handleShow}>
                        <FormControlLabel value="all" control={<Radio size="small" />} label="Toutes" />
                        <FormControlLabel value="selected" control={<Radio size="small" />} label="Sélectionnées" />
                        <FormControlLabel value="unselected" control={<Radio size="small" />} label="Non sélectionnées" />
                        <FormControlLabel value="excluded" control={<Radio size="small" />} label="Exclues" />
                    </RadioGroup>
                </Box>

                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {selection.size} box sélectionnée{selection.size > 1 ? 's' : ''}
                    {outsideCount > 0
                        ? `, dont ${outsideCount} hors de la liste affichée (conservée${outsideCount > 1 ? 's' : ''} à l'enregistrement)`
                        : ` sur ${rows.length}`}
                </Typography>
                {excludedListedCount > 0 && (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }} mt={0.5}>
                        <WarningRounded style={{ color: '#e65100', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#e65100' }}>
                            {excludedListedCount} box sélectionnée{excludedListedCount > 1 ? 's ne sont' : ' n\'est'} pas
                            envoyée{excludedListedCount > 1 ? 's' : ''} (voir le filtre Exclues).
                        </Typography>
                    </Box>
                )}
                {unknownExcludedIds.length > 0 && (
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }} mt={0.5}>
                        <WarningRounded style={{ color: '#e65100', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#e65100' }}>
                            {unknownExcludedIds.length} box sélectionnée{unknownExcludedIds.length > 1 ? 's sont introuvables' : ' est introuvable'} (association
                            supprimée ou d'un autre compte) : décochez-les ou retirez-les.
                        </Typography>
                        <Button size="small" variant="outlined" onClick={handleRemoveUnknown} disabled={saving}>
                            Retirer les introuvables
                        </Button>
                    </Box>
                )}
                {pendingAdditions > 0 && (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }} mt={0.5}>
                        <WarningRounded style={{ color: '#f57f17', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#f57f17' }}>
                            {pendingAdditions} box ajoutée{pendingAdditions > 1 ? 's' : ''} : à l'enregistrement, tout
                            l'historique de leurs paiements depuis la date de départ sera envoyé à la prochaine synchronisation.
                        </Typography>
                    </Box>
                )}

                {loadingRows ? (
                    <Box display="flex" alignItems="center" p={2}>
                        <CircularProgress size={20} />
                        <Box ml={1}>
                            <Typography variant="body2">Chargement des box...</Typography>
                        </Box>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={allFilteredSelected}
                                            indeterminate={someFilteredSelected}
                                            onChange={handleToggleAllFiltered}
                                            disabled={filtered.length === 0 || saving}
                                            inputProps={{ 'aria-label': 'Tout cocher (lignes filtrées)' }}
                                        />
                                    </TableCell>
                                    <TableCell>Paygo Id</TableCell>
                                    <TableCell>Client</TableCell>
                                    <TableCell>Abonnement</TableCell>
                                    <TableCell>Date d'enregistrement</TableCell>
                                    <TableCell>Statut Prospect</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageRows.map((row) => (
                                    <TableRow
                                        key={row.boxAssoId}
                                        hover
                                        onClick={saving ? undefined : () => handleToggle(row.boxAssoId)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selection.has(row.boxAssoId)}
                                                disabled={saving}
                                                // Le clic est déjà géré par la ligne : on évite la double bascule.
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleToggle(row.boxAssoId)}
                                            />
                                        </TableCell>
                                        <TableCell>{row.idBox}</TableCell>
                                        <TableCell>{row.clientName}</TableCell>
                                        <TableCell>{row.subscriptionName}</TableCell>
                                        <TableCell>{formatDate(row.timeCreation)}</TableCell>
                                        <TableCell>{renderStatus(row.boxAssoId)}</TableCell>
                                    </TableRow>
                                ))}
                                {pageRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" color="textSecondary">Aucune box</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filtered.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            labelRowsPerPage="Lignes par page"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
                        />
                    </TableContainer>
                )}

                {saveError && (
                    <Box p={1}>
                        <Typography variant="body2" color="error">{saveError}</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Annuler</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || loadingRows}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function mapStateToProps(state) {
    return {
        CLIENTS: state.clients,
        SUBSCRIPTIONS: state.subscriptions,
        STATE: state.inventory.requestState,
        boxes: state.settings && state.settings.boxes && state.settings.boxes[INTEGRATION_TYPE],
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getPaygClients,
        fetchSubscriptions,
        saveIntegrationBoxes,
        resetIntegrationBoxesSaveError,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ProspectBoxSelectionDialog);
